// Promo carousel.
//
// Forward always travels right, including the wrap. That is the whole reason for
// the clones: a real slide is duplicated at each end, the track moves in one
// direction, and once the movement lands the track snaps silently back to the
// equivalent real slide. Without it, going from the last slide to the first
// animates backwards across the whole track.
class ApsPromo extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-aps-track]');
    if (!this.track) return;
    this.real = [...this.track.querySelectorAll('.slide')];
    this.n = this.real.length;
    this.dots = [...this.querySelectorAll('.dots button')];
    this.interval = Number(this.dataset.interval || 6000);

    // A single slide has nothing to rotate to; leave it as static markup.
    if (this.n < 2) {
      this.querySelectorAll('.nav, .dots').forEach((el) => el.remove());
      return;
    }

    this.track.insertBefore(this.clone(this.real[this.n - 1]), this.real[0]);
    this.track.appendChild(this.clone(this.real[0]));

    this.i = 1;
    this.moving = false;
    this.timer = null;

    this.track.addEventListener('transitionend', (e) => {
      if (e.target !== this.track || e.propertyName !== 'transform') return;
      this.moving = false;
      if (this.i === this.n + 1) { this.i = 1; this.place(false); }
      else if (this.i === 0) { this.i = this.n; this.place(false); }
    });

    this.dots.forEach((d, k) => d.addEventListener('click', () => {
      const cur = ((this.i - 1) % this.n + this.n) % this.n;
      const forward = ((k - cur) % this.n + this.n) % this.n;   // dots travel right too
      if (forward) this.step(forward);
      this.start();
    }));

    this.querySelectorAll('.nav').forEach((b) =>
      b.addEventListener('click', () => { this.step(Number(b.dataset.n)); this.start(); }));

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    this.place(false);
    this.paint();
    this.start();
  }

  disconnectedCallback() { this.stop(); }

  clone(node) {
    const c = node.cloneNode(true);
    // Duplicated ids would be invalid and would break every getElementById and
    // label association on the page.
    c.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    c.removeAttribute('id');
    c.dataset.clone = '1';
    c.setAttribute('aria-hidden', 'true');
    // A clone is decorative. Leaving its links tabbable puts the same
    // destination in the tab order two or three times.
    c.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    return c;
  }

  place(animate) {
    this.track.style.transition = animate ? '' : 'none';
    this.track.style.transform = `translateX(-${this.i * 100}%)`;
    if (!animate) { void this.track.offsetHeight; this.track.style.transition = ''; }
  }

  paint() {
    const r = ((this.i - 1) % this.n + this.n) % this.n;
    this.dots.forEach((d, k) => {
      d.classList.toggle('on', k === r);
      d.setAttribute('aria-current', k === r ? 'true' : 'false');
    });
    this.real.forEach((s, k) => s.setAttribute('aria-hidden', k === r ? 'false' : 'true'));
  }

  step(dir) {
    if (this.moving) return;
    this.moving = true;
    this.i += dir;
    this.place(true);
    this.paint();
  }

  start() {
    this.stop();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.interval <= 0) return;
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
}
customElements.define('aps-promo', ApsPromo);

// "Ships today" countdown to the daily cutoff.
//
// The cutoff is a wall-clock time in the store's timezone, not the visitor's, so
// it is computed from a UTC offset the section supplies rather than from the
// browser's local time -- otherwise a customer in London sees a different answer
// from one in Austin for the same warehouse.
class ApsCutoff extends HTMLElement {
  connectedCallback() {
    this.msg = this.querySelector('[data-aps-cutoff-msg]');
    this.count = this.querySelector('[data-aps-cutoff-count]');
    this.hour = Number(this.dataset.hour ?? 15);
    this.offset = Number(this.dataset.offset ?? -5);   // store timezone, hours from UTC
    this.today = this.dataset.today || 'Ships today';
    this.tomorrow = this.dataset.tomorrow || 'Ships tomorrow';
    // Copy, so it belongs to the merchant rather than to this file. It also carries
    // its own punctuation -- the separator used to be a hard-coded em dash here.
    this.label = this.dataset.label ?? 'order in';
    if (!this.msg && !this.count) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() { if (this.timer) clearInterval(this.timer); }

  tick() {
    const now = new Date();
    // Shift into store-local time, do the arithmetic there, and never touch the
    // visitor's own timezone.
    const storeNow = new Date(now.getTime() + (this.offset * 60 + now.getTimezoneOffset()) * 60000);
    const cutoff = new Date(storeNow);
    cutoff.setHours(this.hour, 0, 0, 0);

    let sameDay = true;
    if (storeNow >= cutoff) { cutoff.setDate(cutoff.getDate() + 1); sameDay = false; }

    const left = Math.max(0, cutoff - storeNow);
    const h = Math.floor(left / 3.6e6);
    const m = Math.floor((left % 3.6e6) / 6e4);
    const s = Math.floor((left % 6e4) / 1000);

    if (this.msg) this.msg.textContent = sameDay ? this.today : this.tomorrow;
    if (this.count) {
      const clock = `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
      this.count.textContent = this.label ? `${this.label} ${clock}` : ` ${clock}`;
    }
  }
}
customElements.define('aps-cutoff', ApsCutoff);
